/**
 * Atlas — Auth context.
 * Provides login/logout/register + current user to the whole app.
 *
 * Token strategy:
 *  - Access token: stored in module memory (_accessToken in api.js), never localStorage.
 *  - Refresh token: lives in an httpOnly cookie set by the backend — JS cannot read it.
 *  - On mount: silent refresh via cookie restores the session without any localStorage read.
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authAPI, setAccessToken, clearAccessToken, silentRefresh } from '../services/api';
import { useIdleLogout } from '../hooks/useIdleLogout';
import IdleWarningModal from '../components/auth/IdleWarningModal';

const AuthContext = createContext(null);

// Sane bounds — must match the backend clamp in /auth/session-config.
const DEFAULT_IDLE_MIN = 10;
const MIN_IDLE_MIN = 1;
const MAX_IDLE_MIN = 240;
const WARN_MS = 60_000; // surface the modal 1 min before logout

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idleMinutes, setIdleMinutes] = useState(DEFAULT_IDLE_MIN);
  // {open, secondsLeft} for the "you're about to be signed out" modal.
  const [idleWarn, setIdleWarn] = useState(null);

  // Restore session on mount using the httpOnly refresh cookie.
  useEffect(() => {
    silentRefresh()
      .then(({ data }) => {
        setAccessToken(data.access_token);
        setUser(data.user);
      })
      .catch(() => {
        // No valid refresh cookie — user must log in.
      })
      .finally(() => setLoading(false));
  }, []);

  // Whenever the user changes (sign in / sign out / silent refresh),
  // pull the current idle-timeout policy from the server. Falls back
  // to the default if the endpoint is unavailable.
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    authAPI.sessionConfig()
      .then(({ data }) => {
        if (cancelled) return;
        const raw = Number(data?.idle_timeout_minutes);
        const clamped = Number.isFinite(raw)
          ? Math.max(MIN_IDLE_MIN, Math.min(MAX_IDLE_MIN, raw))
          : DEFAULT_IDLE_MIN;
        setIdleMinutes(clamped);
      })
      .catch(() => { /* keep default — autocomplete is non-blocking */ });
    return () => { cancelled = true; };
  }, [user]);

  const login = useCallback(async (email, password, mfaCode) => {
    const payload = { email, password };
    if (mfaCode) payload.mfa_code = mfaCode;
    const { data } = await authAPI.login(payload);
    setAccessToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (formData) => {
    const { data } = await authAPI.register(formData);
    setAccessToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      // Revoke tokens server-side and clear the refresh cookie (SOC 2 CC6.1).
      await authAPI.logout();
    } catch (_) {
      // Network failure or already-expired token — still clear local state.
    } finally {
      clearAccessToken();
      setUser(null);
      setIdleWarn(null);
    }
  }, []);

  // Hook fires onWarn 1 minute before timeoutMs is reached, onLogout
  // at the deadline. We keep the modal open for the warn window; if
  // the user clicks "Stay" we call `reset()` to reschedule everything.
  const { reset: resetIdle } = useIdleLogout({
    enabled: !!user,
    timeoutMs: idleMinutes * 60_000,
    warnMs: WARN_MS,
    onWarn: (secondsLeft) => setIdleWarn({ open: true, secondsLeft }),
    onLogout: () => { void logout(); },
  });

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isOwner: user?.role === 'owner',
    isAgent: user?.role === 'agent',
    isBuyer: user?.role === 'buyer',
    isPro: user?.role === 'pro',
    isAdmin: user?.role === 'admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {idleWarn?.open && (
        <IdleWarningModal
          initialSecondsLeft={idleWarn.secondsLeft}
          onStay={() => { setIdleWarn(null); resetIdle(); }}
          onLogoutNow={() => { void logout(); }}
        />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
